import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import { fetchBridgeChainById } from '@/api/modules'
import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import { bridgelessChainInfo } from '@/config'
import { ChainType } from '@/generated/proto/bridge/models/chain'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'
import {
  UiSelect,
  UiSelectContent,
  UiSelectItem,
  UiSelectTrigger,
  UiSelectValue,
} from '@/ui/UiSelect'

import {
  buildInsertChainMessage,
  InsertChainFormValues,
} from './chainMessageBuilder'

const CHAIN_TYPE_OPTIONS = [
  { value: ChainType.EVM, label: 'EVM' },
  { value: ChainType.BITCOIN, label: 'Bitcoin' },
  { value: ChainType.COSMOS, label: 'Cosmos' },
  { value: ChainType.OTHER, label: 'Other' },
  { value: ChainType.ZANO, label: 'Zano' },
  { value: ChainType.TON, label: 'TON' },
  { value: ChainType.SOLANA, label: 'Solana' },
]

const bech32Prefix = bridgelessChainInfo.bech32Config.bech32PrefixAccAddr

const chainAddressRules = (type: ChainType) => {
  switch (type) {
    case ChainType.EVM:
      return {
        placeholder: '0x...',
        validate: (value: string) => /^0x[a-fA-F0-9]{40}$/.test(value),
        message: 'EVM address must be 0x + 40 hex characters',
      }
    case ChainType.BITCOIN:
      return {
        placeholder: 'bc1...',
        validate: (value: string) =>
          /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,59}$/.test(value),
        message: 'Bitcoin address must be valid (bc1/1/3 prefix)',
      }
    case ChainType.COSMOS:
      return {
        placeholder: `${bech32Prefix}1...`,
        validate: (value: string) =>
          new RegExp(`^${bech32Prefix}1[0-9a-z]{38,}$`).test(value),
        message: `Address must be valid ${bech32Prefix}1... bech32`,
      }
    case ChainType.TON:
      return {
        placeholder: 'EQ...',
        validate: (value: string) => /^(EQ|UQ)[A-Za-z0-9_-]{46}$/.test(value),
        message: 'TON address must be EQ/UQ base64url',
      }
    case ChainType.SOLANA:
      return {
        placeholder: 'Base58...',
        validate: (value: string) =>
          /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(value),
        message: 'Solana address must be base58',
      }
    case ChainType.ZANO:
      return {
        placeholder: 'Zx...',
        validate: (value: string) => /^Zx[a-zA-Z0-9]{95}$/.test(value),
        message: 'Zano address must start with Zx and be 97 chars',
      }
    default:
      return {
        placeholder: 'Address...',
        validate: (value: string) => value.trim().length > 0,
        message: 'Address is required',
      }
  }
}

const insertChainSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  chain: z
    .object({
      id: z
        .string()
        .min(1, 'Chain ID is required')
        .refine(
          async chainId => {
            try {
              await fetchBridgeChainById(chainId)
              return false
            } catch {
              return true
            }
          },
          { message: 'Chain with this ID already exists' },
        ),
      type: z.nativeEnum(ChainType),
      bridgeAddress: z.string().min(1, 'Bridge address is required'),
      operator: z.string().min(1, 'Operator address is required'),
      confirmations: z.union([z.string(), z.number()]),
      name: z.string().min(1, 'Chain name is required'),
    })
    .superRefine((value, ctx) => {
      const rules = chainAddressRules(value.type)

      if (value.bridgeAddress && !rules.validate(value.bridgeAddress)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: rules.message,
          path: ['bridgeAddress'],
        })
      }

      if (value.operator && !rules.validate(value.operator)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: rules.message,
          path: ['operator'],
        })
      }

      const confirmationsNum =
        typeof value.confirmations === 'number'
          ? value.confirmations
          : Number(value.confirmations.trim())

      if (!Number.isInteger(confirmationsNum) || confirmationsNum < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Confirmations must be greater than 0',
          path: ['confirmations'],
        })
      }
    }),
})

export default function InsertChainForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<InsertChainFormValues>({
    resolver: zodResolver(insertChainSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      chain: {
        id: '',
        type: ChainType.EVM,
        bridgeAddress: '',
        operator: '',
        confirmations: 1,
        name: '',
      },
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const selectedChainType = form.watch('chain.type')
  const chainAddressMeta = chainAddressRules(selectedChainType)

  useEffect(() => {
    const bridge = form.getValues('chain.bridgeAddress')
    const operator = form.getValues('chain.operator')

    if (
      (bridge && bridge.toString().trim() !== '') ||
      (operator && operator.toString().trim() !== '')
    ) {
      void form.trigger(['chain.bridgeAddress', 'chain.operator'])
    }
  }, [selectedChainType, form])

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildInsertChainMessage(watchedValues as InsertChainFormValues)
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Insert Chain Message
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to generate a message for inserting a new chain
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            placeholder='Connect wallet'
            readOnly
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
            {...form.register('creator')}
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>Chain Details</h4>

          <div className='flex flex-col gap-3'>
            <div className='flex flex-wrap gap-3 lg:flex-nowrap'>
              <div className='min-w-45 flex-1 flex-col gap-3'>
                <UiLabel htmlFor='id'>Chain ID</UiLabel>
                <GradientWrapper>
                  <UiInput
                    id='id'
                    placeholder='ethereum'
                    disabled={isSubmitting}
                    className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                    {...form.register('chain.id')}
                  />
                </GradientWrapper>
                {form.formState.errors.chain?.id && (
                  <p className='text-destructive text-sm'>
                    {form.formState.errors.chain.id.message}
                  </p>
                )}
              </div>

              <div className='flex min-w-45 flex-1 flex-col gap-3'>
                <UiLabel htmlFor='name'>Chain Name</UiLabel>
                <GradientWrapper>
                  <UiInput
                    id='name'
                    placeholder='Ethereum'
                    disabled={isSubmitting}
                    className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                    {...form.register('chain.name')}
                  />
                </GradientWrapper>
                {form.formState.errors.chain?.name && (
                  <p className='text-destructive text-sm'>
                    {form.formState.errors.chain.name.message}
                  </p>
                )}
              </div>

              <div className='flex w-full min-w-50 flex-col gap-3 lg:flex-0'>
                <UiLabel htmlFor='type'>Chain Type</UiLabel>
                <GradientWrapper>
                  <UiSelect
                    value={form.watch('chain.type').toString()}
                    disabled={isSubmitting}
                    onValueChange={value =>
                      form.setValue(
                        'chain.type',
                        parseInt(value) as ChainType,
                        {
                          shouldDirty: true,
                          shouldValidate: true,
                        },
                      )
                    }
                  >
                    <UiSelectTrigger
                      size='xl'
                      className='text-foreground w-full'
                      id='type'
                    >
                      <UiSelectValue />
                    </UiSelectTrigger>
                    <UiSelectContent>
                      {CHAIN_TYPE_OPTIONS.map(option => (
                        <UiSelectItem
                          key={option.value}
                          value={option.value.toString()}
                        >
                          {option.label}
                        </UiSelectItem>
                      ))}
                    </UiSelectContent>
                  </UiSelect>
                </GradientWrapper>
                {form.formState.errors.chain?.type && (
                  <p className='text-destructive text-sm'>
                    {form.formState.errors.chain.type.message}
                  </p>
                )}
              </div>
            </div>

            <div className='flex flex-col gap-6'>
              <div className='flex flex-col gap-2'>
                <UiLabel htmlFor='bridgeAddress'>Bridge Address</UiLabel>
                <GradientWrapper>
                  <UiInput
                    id='bridgeAddress'
                    placeholder={chainAddressMeta.placeholder}
                    disabled={isSubmitting}
                    {...form.register('chain.bridgeAddress')}
                  />
                </GradientWrapper>
                {form.formState.errors.chain?.bridgeAddress && (
                  <p className='text-destructive text-sm'>
                    {form.formState.errors.chain.bridgeAddress.message}
                  </p>
                )}
              </div>

              <div className='flex flex-col gap-2'>
                <UiLabel htmlFor='operator'>Operator Address</UiLabel>
                <GradientWrapper>
                  <UiInput
                    id='operator'
                    placeholder={chainAddressMeta.placeholder}
                    disabled={isSubmitting}
                    {...form.register('chain.operator')}
                  />
                </GradientWrapper>
                {form.formState.errors.chain?.operator && (
                  <p className='text-destructive text-sm'>
                    {form.formState.errors.chain.operator.message}
                  </p>
                )}
              </div>

              <div className='flex flex-col gap-2'>
                <UiLabel htmlFor='confirmations'>Confirmations</UiLabel>
                <GradientWrapper>
                  <UiInput
                    id='confirmations'
                    type='number'
                    placeholder='12'
                    disabled={isSubmitting}
                    {...form.register('chain.confirmations')}
                  />
                </GradientWrapper>
                {form.formState.errors.chain?.confirmations && (
                  <p className='text-destructive text-sm'>
                    {form.formState.errors.chain.confirmations.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(messagePayload, 'insert-chain-message.json')
                }
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildInsertChainMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
